import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Paper,
  Table,
  Badge,
  Tabs,
  Loader,
  Alert,
  Pagination,
  Stack,
  Select,
  TextInput,
  Card,
  ActionIcon
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconEye,
  IconFileInvoice,
  IconTruckDelivery,
  IconShoppingBag,
  IconAlertCircle,
  IconPackage
} from '@tabler/icons-react';

// Order type definition
interface Order {
  id: string;
  date: string;
  status: 'processing' | 'shipped' | 'delivered' | 'cancelled';
  total: number;
  items: number;
  trackingNumber?: string;
  estimatedDelivery?: string;
}

const MyOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>('all');
  const [activePage, setActivePage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>('all');

  // Mock data for orders
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockOrders: Order[] = [
        {
          id: 'ORD-12345678',
          date: '2025-04-05T14:30:00Z',
          status: 'processing',
          total: 127.47,
          items: 2
        },
        {
          id: 'ORD-87654321',
          date: '2025-03-20T10:15:00Z',
          status: 'shipped',
          total: 56.98,
          items: 1,
          trackingNumber: 'TRK-9876543210',
          estimatedDelivery: '2025-04-02'
        },
        {
          id: 'ORD-23456789',
          date: '2025-03-10T09:45:00Z',
          status: 'delivered',
          total: 89.25,
          items: 3,
          trackingNumber: 'TRK-2345678901'
        },
        {
          id: 'ORD-98765432',
          date: '2025-02-28T16:20:00Z',
          status: 'delivered',
          total: 42.50,
          items: 1,
          trackingNumber: 'TRK-8765432109'
        },
        {
          id: 'ORD-34567890',
          date: '2025-02-15T11:05:00Z',
          status: 'cancelled',
          total: 110.99,
          items: 2
        }
      ];

      setOrders(mockOrders);
      setLoading(false);
    }, 1000);
  }, []);

  // Filter orders based on tab, search query, and status filter
  const filteredOrders = orders.filter(order => {
    // Filter by tab
    if (activeTab !== 'all' && order.status !== activeTab) {
      return false;
    }

    // Filter by search query
    if (
      searchQuery &&
      !order.id.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // Filter by status
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }

    return true;
  });

  // Pagination
  const ordersPerPage = 10;
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = filteredOrders.slice(
    (activePage - 1) * ordersPerPage,
    activePage * ordersPerPage
  );

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'processing':
        return 'blue';
      case 'shipped':
        return 'yellow';
      case 'delivered':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (

      <Container size="xl" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Loader size="lg" />
        </div>
      </Container>

    );
  }

  return (

    <Container size="xl" py="xl">
      <Group mb="xl">
        <div>
          <Title order={1}>My Orders</Title>
          <Text c="dimmed">
            View and track all your Sri Lankan product orders
          </Text>
        </div>
        <Button
          component={Link}
          to="/products"
          leftSection={<IconShoppingBag size={16} />}
        >
          Browse Products
        </Button>
      </Group>

      <Card withBorder radius="md" p="md" mb="xl">
        <Group mb="md">
          <Title order={3}>Order History</Title>

          <Group>
            <TextInput
              placeholder="Search by Order ID"
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              style={{ width: 200 }}
            />

            <Select
              placeholder="Filter by Status"
              leftSection={<IconFilter size={16} />}
              data={[
                { value: 'all', label: 'All Orders' },
                { value: 'processing', label: 'Processing' },
                { value: 'shipped', label: 'Shipped' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            />
          </Group>
        </Group>

        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          mb="lg"
        >
          <Tabs.List>
            <Tabs.Tab value="all">All Orders</Tabs.Tab>
            <Tabs.Tab value="processing">Processing</Tabs.Tab>
            <Tabs.Tab value="shipped">Shipped</Tabs.Tab>
            <Tabs.Tab value="delivered">Delivered</Tabs.Tab>
            <Tabs.Tab value="cancelled">Cancelled</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {paginatedOrders.length === 0 ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="No orders found"
            color="blue"
            variant="light"
          >
            <Text>
              {searchQuery || statusFilter !== 'all'
                ? 'No orders match your search criteria. Try adjusting your filters.'
                : 'You haven\'t placed any orders yet. Start shopping to see your orders here.'}
            </Text>
          </Alert>
        ) : (
          <Paper withBorder radius="md" p={0}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Order ID</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Tracking</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginatedOrders.map(order => (
                  <Table.Tr key={order.id}>
                    <Table.Td>
                      <Text fw={500}>{order.id}</Text>
                      <Text size="xs" c="dimmed">{order.items} item(s)</Text>
                    </Table.Td>
                    <Table.Td>{formatDate(order.date)}</Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(order.status)}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>${order.total.toFixed(2)}</Table.Td>
                    <Table.Td>
                      {order.trackingNumber ? (
                        <Group >
                          <IconTruckDelivery size={16} />
                          <Text size="sm">{order.trackingNumber}</Text>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group>
                        <ActionIcon
                          component={Link}
                          to={`/my-orders/${order.id}`}
                          color="blue"
                          variant="light"
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                        {order.status !== 'cancelled' && (
                          <ActionIcon
                            component={Link}
                            to={`/my-orders/${order.id}/invoice`}
                            color="gray"
                            variant="light"
                          >
                            <IconFileInvoice size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        {totalPages > 1 && (
          <Group mt="lg">
            <Pagination
              total={totalPages}
              value={activePage}
              onChange={setActivePage}
            />
          </Group>
        )}
      </Card>

      <Stack>
        <Paper withBorder p="lg" radius="md">
          <Group >
            <IconPackage size={24} color="#228be6" />
            <div>
              <Text fw={500}>Need to consolidate with other packages?</Text>
              <Text size="sm">
                Visit your <Link to="/packages">Packages dashboard</Link> to consolidate
                these products with other items in your OTLExpress account.
              </Text>
            </div>
          </Group>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Group >
            <IconTruckDelivery size={24} color="#228be6" />
            <div>
              <Text fw={500}>Track your deliveries in one place</Text>
              <Text size="sm">
                All your orders, including consolidated shipments, are available in your
                <Link to="/shipments"> Shipments dashboard</Link>.
              </Text>
            </div>
          </Group>
        </Paper>
      </Stack >
    </Container >

  );
};

export default MyOrders;
